import * as trace from 'owa-trace';
import convertCalendarEventsToAgendaItems from '../../utils/convertCalendarEventsToAgendaItems';
import datapoints from '../../datapoints';
import debounce from 'lodash-es/debounce';
import getAgendaKeysBasedOnPivotTime from '../../utils/getAgendaKeysBasedOnPivotTime';
import markPriorityForAgendaItems from 'mini-calendar-agenda-store/lib/utils/markPriorityForAgendaItems';
import saveAgendaState from '../../utils/saveAgendaState';
import { AgendaItem } from 'mini-agenda';
import { createAppContext } from 'mini-app-context';
import { getDateString, getTimestamp, OwaDate, startOfDay } from 'owa-datetime';
import { MAX_EVENTS_TO_FETCH, SUFFIX_AGENDA_SEPARATOR } from '../../utils/agendaConstants';
import { setAnchorKeyInfo } from '../../utils/anchorKeyUtils';
import { setIsLoading } from 'mini-calendar-agenda-store/lib/mutators/setIsLoading';
import { setPriorityRenderingOverrideState } from '../../utils/priorityRenderingUtils';
import {
    onAgendaViewLoadingStatusChanged,
    onAgendaViewDateChanged,
} from '../../actions/publicActions';
import {
    getCalendarEventsForRange,
    GetCalendarEventsForRangeResponse,
    CalendarFetchOrder,
    convertDateRangeToDisplayDateRange,
} from 'mini-calendar-events-store';

let loadAgendaItemsAroundPivotVersionId = 0;

export const getVersionId = () => loadAgendaItemsAroundPivotVersionId;

export const updateVersionId = () => loadAgendaItemsAroundPivotVersionId++;

/**
 * Functions which gets the events for a given pivot and sets the agenda items
 * @param pivotDate DisplayDate which is to be used as pivot.
 * @param updateAnchorKey Boolean value to indicate whether to update the anchor key or not.
 * This makes sure that pivot in satchel layer and anchor element in view layer are used for seperate concerns.
 */
export const loadAgendaItemsAroundPivotInternal = async function (
    pivotDate: OwaDate,
    updateAnchorKey: boolean = true
): Promise<void> {
    // Set an internal state flag to indicate that the loadAgendaItemsAroundPivot is in progress, so as to
    // ignore any new call triggered by reloadAgendaItems. This is different from the viewState spinner flag,
    // which is set with a delay of 200 ms; race conditions can happen even in that 200 ms delay.
    setIsLoading(true);
    updateVersionId();

    const versionId = getVersionId();

    // Generate the keys for the pivot time
    const { topOfPivotKey, pivotKey, bottomOfPivotKey } = getAgendaKeysBasedOnPivotTime(pivotDate);

    const topAppContext = createAppContext();
    const bottomAppContext = createAppContext();

    topAppContext.signal(datapoints.AGENDA_VIEW_STORE.LOAD_AROUND_PIVOT);
    bottomAppContext.signal(datapoints.AGENDA_VIEW_STORE.LOAD_AROUND_PIVOT);

    // Get the events for top of the pivot.
    const pivotTopEventsFetchPromise = getCalendarEventsForRange(
        {
            start: topOfPivotKey,
            end: pivotKey,
        },
        CalendarFetchOrder.Descending /* Descending (from end date to start date) to get events density near pivot key*/,
        MAX_EVENTS_TO_FETCH / 2 /* minEventsCount */,
        MAX_EVENTS_TO_FETCH / 2 /* maxEventsCount */,
        topAppContext
    );

    // Get the events for the bottom of the pivot
    const pivotBottomEventsFetchPromise = getCalendarEventsForRange(
        {
            start: pivotKey,
            end: bottomOfPivotKey,
        },
        CalendarFetchOrder.Ascending /* Ascending (from start date to end date) to get events density near pivot key */,
        MAX_EVENTS_TO_FETCH / 2 /* minEventsCount */,
        MAX_EVENTS_TO_FETCH / 2 /* maxEventsCount */,
        bottomAppContext
    );

    const startOfDayTime = startOfDay(pivotDate);
    const startOfDayTimestamp = getTimestamp(startOfDayTime);
    const anchorKey = startOfDayTimestamp + SUFFIX_AGENDA_SEPARATOR;

    let timer: NodeJS.Timer;
    const loadItemsPromise = Promise.all([
        pivotTopEventsFetchPromise,
        pivotBottomEventsFetchPromise,
    ])
        .then(function (values: GetCalendarEventsForRangeResponse[]) {
            // clear the timer if the new events are fetched within the expected delay
            clearTimeout(timer);
            // There is a newer loadAgendaItemsAroundPivot call so old one can be discarded
            if (getVersionId() !== versionId) {
                return;
            }

            const { events: eventsAtTopOfPivot, dateRange: dateRangeAtTopOfPivot } = values[0];
            topAppContext.signal(datapoints.AGENDA_VIEW_STORE.LOAD_AROUND_PIVOT_RESPONSE, {
                eventsLength: eventsAtTopOfPivot.length,
                firstEventStartDate:
                    eventsAtTopOfPivot[0] &&
                    eventsAtTopOfPivot[0].Start &&
                    getDateString(eventsAtTopOfPivot[0].Start),
                lastEventStartDate:
                    eventsAtTopOfPivot[eventsAtTopOfPivot.length - 1] &&
                    eventsAtTopOfPivot[eventsAtTopOfPivot.length - 1].Start &&
                    getDateString(eventsAtTopOfPivot[eventsAtTopOfPivot.length - 1].Start),
                startDateAtTopOfPivot:
                    dateRangeAtTopOfPivot && new Date(dateRangeAtTopOfPivot.start).toDateString(),
                endDateAtTopOfPivot:
                    dateRangeAtTopOfPivot && new Date(dateRangeAtTopOfPivot.end).toDateString(),
            });
            const {
                events: eventsAtBottomOfPivot,
                dateRange: dateRangeAtBottomOfPivot,
            } = values[1];
            bottomAppContext.signal(datapoints.AGENDA_VIEW_STORE.LOAD_AROUND_PIVOT_RESPONSE, {
                eventsLength: eventsAtBottomOfPivot.length,
                firstEventStartDate:
                    eventsAtBottomOfPivot[0] &&
                    eventsAtBottomOfPivot[0].Start &&
                    getDateString(eventsAtBottomOfPivot[0].Start),
                lastEventStartDate:
                    eventsAtBottomOfPivot[eventsAtBottomOfPivot.length - 1] &&
                    eventsAtBottomOfPivot[eventsAtBottomOfPivot.length - 1].Start &&
                    getDateString(eventsAtBottomOfPivot[eventsAtBottomOfPivot.length - 1].Start),
                startDateAtBottomOfPivot:
                    dateRangeAtBottomOfPivot &&
                    new Date(dateRangeAtBottomOfPivot.start).toDateString(),
                endDateAtBottomOfPivot:
                    dateRangeAtBottomOfPivot &&
                    new Date(dateRangeAtBottomOfPivot.end).toDateString(),
            });

            // Splitting this to avoid incorrect date range computation when not enough events on one side of pivot call
            // but got all the events on other side.
            const { start: startDayTop } = convertDateRangeToDisplayDateRange(
                dateRangeAtTopOfPivot
            );
            const { end: endDayBottom } = convertDateRangeToDisplayDateRange(
                dateRangeAtBottomOfPivot
            );

            // Add the events received from top and bottom of the pivot.
            // This is based on the contract that the two api calls based on pivot do not return duplicate events.
            const events = eventsAtTopOfPivot.concat(eventsAtBottomOfPivot);

            // Generate the agenda items using the events and the distinct days
            let agendaItems: AgendaItem[] = convertCalendarEventsToAgendaItems(
                events,
                startDayTop,
                endDayBottom
            );

            agendaItems = markPriorityForAgendaItems(agendaItems, anchorKey);

            // Set anchor key only when pivot date has to be reflected in scroll view as anchor element
            updateAnchorKey && setAnchorKeyInfo(anchorKey, startOfDayTimestamp, true);
            setPriorityRenderingOverrideState(
                false /** Remove any overrides so that normal priority takes over */
            );

            // Save the agenda items in the store and the current date at top.
            saveAgendaState(agendaItems, startOfDayTime);

            onAgendaViewLoadingStatusChanged(false);
            setIsLoading(false);
        })
        .catch(error => {
            if (error !== 'REJECT_AS_STALE') {
                trace.errorThatWillCauseAlert(`[loadAgendaItemsAroundPivot]: ${error}`);
            }
            setIsLoading(false);
            // TODO: Revisit the logic after the design input. For now, the spinner keeps showing.
        });
    // Show spinner, if the service call to fetch the events doesn't finish in 200ms
    timer = setTimeout(() => {
        // Need to set dateAtTop for reload to work
        onAgendaViewDateChanged(startOfDayTime);
        // Reset anchor key also
        updateAnchorKey && setAnchorKeyInfo(anchorKey, startOfDayTimestamp, true);

        onAgendaViewLoadingStatusChanged(true);
    }, 200);

    return loadItemsPromise;
};

export default debounce(loadAgendaItemsAroundPivotInternal, 100);
